<script setup lang="ts">
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

const props = withDefaults(
  defineProps<{
    /** 缺少的权限 key，如 campaign.view */
    permission: string | string[]
    /** 覆盖权限中文名 */
    permissionLabel?: string
  }>(),
  {}
)

const authStore = useAuthStore()

const PERM_LABELS: Record<string, string> = {
  'shop.view': '查看店铺概况',
  'shop.config.view': '查看店铺配置',
  'shop.config.manage': '修改店铺配置',
  'campaign.view': '查看全部计划',
  'campaign.manage': '管理计划（启停/改名等）',
  'budget.manage': '修改预算',
  'roi.manage': '修改 ROI',
  'algorithm.view': '查看算法配置',
  'algorithm.manage': '修改算法配置/开关',
  'auto_placement.view': '查看智能投放',
  'auto_placement.manage': '修改智能投放',
  'product.view': '查看商品列表与成本',
  'product.manage': '修改商品成本',
  'material.view': '查看素材/素材排查',
  'rpa.view': '查看 RPA 配置',
  'rpa.manage': '管理 RPA 配置',
  'invalid_campaign.view': '查看无效计划',
  'invalid_campaign.manage': '管理无效计划',
  'package.view': '查看套餐中心',
  'package.manage': '管理套餐中心',
  'message.view': '查看消息中心',
  'message.manage': '管理消息中心',
  '__org_page_denied__': '该页面（代管模式下不可用）',
}

const requiredList = computed(() =>
  Array.isArray(props.permission) ? props.permission : [props.permission]
)

const permLabelText = computed(() => {
  if (props.permissionLabel) return props.permissionLabel
  return requiredList.value
    .map((k) => PERM_LABELS[k] || k)
    .join(' 或 ')
})

const isPageDenied = computed(() => requiredList.value.includes('__org_page_denied__'))

const resultDescription = computed(() =>
  isPageDenied.value
    ? '代管模式下不开放此功能'
    : '当前服务授权未包含本页所需权限'
)

const shopLabel = computed(() => {
  const info = authStore.orgImpersonation
  if (!info) return '对应店铺'
  if (info.shop_name) return `${info.shop_name}（${info.shop_id}）`
  return `店铺 ${info.shop_id}`
})
</script>

<template>
  <div class="org-no-perm">
    <n-result status="403" title="没有访问权限" :description="resultDescription">
      <template #footer>
        <div v-if="isPageDenied" class="org-no-perm-hint">
          <p class="org-no-perm-sub">数据报表、操作日志等页面仅客户主账号使用；请使用侧栏其它功能继续代管。</p>
        </div>
        <div v-else class="org-no-perm-hint">
          <p>请客户主账号打开：</p>
          <p class="org-no-perm-steps">
            <strong>服务授权</strong>
            → 找到店铺 <strong>{{ shopLabel }}</strong>
            → 点击「改范围」
            → 勾选 <strong>{{ permLabelText }}</strong>
          </p>
          <p class="org-no-perm-sub">保存后，从组织门户重新进入代管即可生效。</p>
        </div>
      </template>
    </n-result>
  </div>
</template>

<style scoped>
.org-no-perm {
  padding: 48px 24px;
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.org-no-perm-hint {
  text-align: center;
  line-height: 1.7;
  color: var(--n-text-color-2);
  max-width: 560px;
}
.org-no-perm-steps {
  margin: 12px 0;
  font-size: 14px;
  color: var(--n-text-color-1);
}
.org-no-perm-sub {
  margin: 0;
  font-size: 12px;
  color: var(--n-text-color-3);
}
</style>
